import { DateUtil } from './DateUtil';
import { Backend } from '../../api/Backend';

/**
 * Formats JS Date to DateTime ISO format (YYYY-MM-DD'T'HH:mm(:ss)'Z')
 * @param {Date} date
 * @param {boolean} withSeconds
 * @returns {string}
 */
export function formatDate(date: Date, withSeconds = false): string {
  const formatted = date.toISOString();
  return `${formatted.slice(0, formatted.lastIndexOf(`${withSeconds ? '.' : ':'}`))}Z`;
}

/**
 * Converts DateTime ISO format (YYYY-MM-DD'T'HH:mm'Z') string to Date object
 * @param {string} dateTime
 * @returns {Date}
 */
export function convertDateTime(dateTime: string): Date {
  return new Date(dateTime);
}

/**
 * Returns time of date in HH:mm format
 * @param {Date} date
 * @returns {string}
 */
export function getTime(date: Date): string {
  return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
}

/**
 * Returns date in format DD/MM
 */
export function formatDayMonth(date: Date): string {
  return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
}

/**
 * Returns duration of date in (HH:)mm:ss format
 * @param {number} time
 * @returns {string}
 */
export function getDuration(time: number): string {
  const sign = time < 0 ? '-' : '';
  const absTime = Math.abs(time);
  const hours = Math.floor(absTime / 3600);
  const hoursString = hours > 0 ? `${hours.toString().padStart(2, '0')}:` : '';
  const mins = Math.floor((absTime % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const secs = (absTime % 60).toString().padStart(2, '0');
  return `${sign}${hoursString}${mins}:${secs}`;
}

/**
 * @param {Date} date
 * @param {boolean} dayShort
 * @param {Backend} backend
 * @returns {string} format examples: tomorrow  or Wednesday 13 april
 */
export function getDayInfoString(date: Date, dayShort: boolean, backend: Backend): string {
  if (!date) return '';

  const now = DateUtil.toStartOfDay(new Date());
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const then = DateUtil.toStartOfDay(new Date(date));

  let result = '';
  if (DateUtil.isSameDay(now, then)) {
    result += DateUtil.getYesterdayTodayTomorrowTranslation(0, backend);
  } else if (DateUtil.isSameDay(tomorrow, then)) {
    result += DateUtil.getYesterdayTodayTomorrowTranslation(1, backend);
  } else if (DateUtil.isSameDay(yesterday, then)) {
    result += DateUtil.getYesterdayTodayTomorrowTranslation(-1, backend);
  } else {
    if (dayShort) {
      result += DateUtil.getShortDayTranslation(then.getDay(), backend);
    } else {
      result += DateUtil.getDayTranslation(then.getDay(), backend);
    }

    result += ' ' + then.getDate().toString().padStart(2, '0');
    result += ' ' + DateUtil.getMonthTranslation(then.getMonth(), backend);
  }

  return result;
}

/**
 * @param {Backend} backend
 * @param {Date} start
 * @param {Date} end
 * @returns {string}
 */
export function formatPlayerSchedule(backend: Backend, start: Date, end: Date): string {
  const startDay = DateUtil.toStartOfDay(new Date(start));

  let result = '';
  result = getDayTranslation(backend, startDay);
  result += ' ' + getTime(start) + ' - ' + getTime(end);
  return result;
}

function getDayTranslation(backend: Backend, date: Date) {
  const now = DateUtil.toStartOfDay(new Date());
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (DateUtil.isSameDay(now, date)) {
    return DateUtil.getYesterdayTodayTomorrowTranslation(0, backend);
  } else if (DateUtil.isSameDay(tomorrow, date)) {
    return DateUtil.getYesterdayTodayTomorrowTranslation(1, backend);
  } else if (DateUtil.isSameDay(yesterday, date)) {
    return DateUtil.getYesterdayTodayTomorrowTranslation(-1, backend);
  }
  let result = DateUtil.getShortDayTranslation(date.getDay(), backend);
  result += ' ' + date.getDate().toString().padStart(2, '0');
  result += ' ' + DateUtil.getMonthTranslation(date.getMonth(), backend);
  return result;
}

export function timeStringToMs(time: string): number {
  if (time.toString().match(/[^0-9.]/)) {
    return new Date(time).getTime();
  } else {
    return (parseInt(time, 10) || 0) * 1000;
  }
}

/**
 * @param {Backend} backend
 * @param {Date} start
 * @returns {string}
 */
export function formatCardSchedule(backend: Backend, start: Date): string {
  const startDay = DateUtil.toStartOfDay(new Date(start));

  let result = getDayTranslation(backend, startDay);
  result += ' ' + getTime(start);
  return result;
}

export function formatOldCardSchedule(backend: Backend, start: Date, end: Date): string {
  const startDay = DateUtil.toStartOfDay(new Date(start));
  let result = `${formatDayMonth(startDay)}, `;
  result += `${getTime(start)} - ${getTime(end)}`;

  return result;
}

export function formatTime(start: Date, end: Date): string {
  return `${getTime(start)} - ${getTime(end)}`;
}
